import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Play, Loader2, Zap } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AVATAR_COLORS = [
  "bg-forkyeah-primary",
  "bg-forkyeah-accent",
  "bg-green-500",
  "bg-blue-500",
  "bg-purple-500",
  "bg-pink-500",
];

function ParticipantAvatar({ participant, index, isNew }) {
  const colorClass = AVATAR_COLORS[index % AVATAR_COLORS.length];
  const initial = participant.name.charAt(0).toUpperCase();

  return (
    <motion.div
      initial={isNew ? { scale: 0, opacity: 0 } : { scale: 1, opacity: 1 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: "spring", duration: 0.5 }}
      className="flex flex-col items-center gap-1"
    >
      <div className={`w-14 h-14 rounded-full ${colorClass} flex items-center justify-center shadow-lg`}>
        <span className="text-xl font-black text-white">{initial}</span>
      </div>
      <span className="text-xs font-bold text-forkyeah-main truncate max-w-[60px]">
        {participant.name}
      </span>
      {participant.is_host && (
        <span className="text-[10px] font-bold text-forkyeah-primary">Host</span>
      )}
    </motion.div>
  );
}

export default function LobbyPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joinMessages, setJoinMessages] = useState([]);
  const previousParticipantsRef = useRef([]);
  const isHost = sessionStorage.getItem("isHost") === "true";

  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sessions/${sessionId}/status`);
      const newParticipants = response.data.participants;
      
      // Check for new participants
      const prevIds = new Set(previousParticipantsRef.current.map(p => p.id));
      const newJoiners = newParticipants.filter(p => !prevIds.has(p.id));
      
      if (newJoiners.length > 0 && previousParticipantsRef.current.length > 0) {
        newJoiners.forEach(p => {
          setJoinMessages(prev => [...prev, `${p.name} joined!`]);
          setTimeout(() => {
            setJoinMessages(prev => prev.slice(1));
          }, 3000);
        });
      }
      
      previousParticipantsRef.current = newParticipants;
      setParticipants(newParticipants);
      
      // Check for match
      if (response.data.latest_match) {
        sessionStorage.setItem("latestMatch", JSON.stringify(response.data.latest_match));
        navigate(`/match/${sessionId}`);
      }
    } catch (error) {
      console.error("Failed to fetch status:", error);
    } finally {
      setLoading(false);
    }
  }, [sessionId, navigate]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 2000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const startSwiping = () => {
    navigate(`/swipe/${sessionId}`);
  };

  if (loading) {
    return (
      <main className="app-container">
        <div className="flex-1 flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-forkyeah-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </main>
    );
  }

  return (
    <main className="app-container">
      <div className="flex-1 flex flex-col px-6 py-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <h1 className="text-2xl font-black text-forkyeah-main mb-1">
            Waiting room
          </h1>
          <p className="text-forkyeah-muted font-semibold text-sm">
            {participants.length === 1 
              ? "Waiting for others to join..." 
              : `${participants.length} people ready`}
          </p>
        </motion.div>

        {/* Participant Count Circle */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="flex justify-center mb-6"
        >
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-forkyeah-primary/10 flex items-center justify-center">
              <div className="text-center">
                <span className="text-4xl font-black text-forkyeah-primary" data-testid="participant-count">
                  {participants.length}
                </span>
                <p className="text-xs font-bold text-forkyeah-muted -mt-1">joined</p>
              </div>
            </div>
            {/* Pulsing ring */}
            <div className="absolute inset-0 rounded-full border-2 border-forkyeah-primary/30 animate-ping" />
          </div>
        </motion.div>

        {/* Join Messages */}
        <AnimatePresence>
          {joinMessages.map((msg, i) => (
            <motion.div
              key={`${msg}-${i}`}
              initial={{ opacity: 0, y: -10, height: 0 }}
              animate={{ opacity: 1, y: 0, height: "auto" }}
              exit={{ opacity: 0, y: 10, height: 0 }}
              className="bg-forkyeah-accent/30 rounded-full px-4 py-2 mb-3 text-center"
            >
              <span className="text-sm font-bold text-forkyeah-main">{msg}</span>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Participant Avatars */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex-1"
        >
          <div className="flex flex-wrap justify-center gap-4">
            {participants.map((p, index) => (
              <ParticipantAvatar
                key={p.id}
                participant={p}
                index={index}
                isNew={false}
              />
            ))}
          </div>
        </motion.div>

        {/* Waiting indicator */}
        <div className="flex items-center justify-center gap-2 py-4">
          <Loader2 className="w-4 h-4 text-forkyeah-muted animate-spin" />
          <span className="text-sm text-forkyeah-muted font-semibold">
            Live updates enabled
          </span>
        </div>

        {/* Start Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <button
            data-testid="start-swiping-btn"
            onClick={startSwiping}
            className="btn-primary gap-2"
          >
            <Zap className="w-5 h-5" strokeWidth={2.5} />
            {isHost ? "Start swiping" : "Ready to swipe"}
          </button>
          {participants.length < 2 && (
            <p className="text-center text-xs text-forkyeah-muted mt-3 font-medium">
              Waiting for at least one more person...
            </p>
          )}
        </motion.div>
      </div>
    </main>
  );
}
